import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Presentation, Trash2, Play, Edit, Plus, HardDrive, Settings, LogOut, ArrowLeft } from 'lucide-react';
import { getUserPresentations, deletePresentation, createSession } from '../services/api';

const PresentationsDashboard = ({ userId }) => {
  const navigate = useNavigate();
  const [presentations, setPresentations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [stats, setStats] = useState({
    total_count: 0,
    total_storage_mb: 0,
    max_presentations: 50,
    max_storage_mb: 500
  });
  const [error, setError] = useState('');
  const [showUserMenu, setShowUserMenu] = useState(false);
  
  const username = localStorage.getItem('username') || 'User';

  const getInitials = (name) => {
    if (!name) return 'U';
    return name
      .split(' ')
      .map(part => part[0])
      .join('')
      .toUpperCase()
      .slice(0, 2);
  };

  const handleLogout = () => {
    localStorage.removeItem('access_token');
    localStorage.removeItem('user_id');
    localStorage.removeItem('username');
    window.location.href = '/';
  };

  useEffect(() => {
    loadPresentations();
  }, [userId]);

  const loadPresentations = async () => {
    try {
      setLoading(true);
      console.log('Loading presentations for user:', userId);
      const data = await getUserPresentations(userId);
      console.log('Presentations loaded:', {
        count: data.total_count,
        presentations: data.presentations?.map(p => ({ id: p.id, title: p.title }))
      });
      setPresentations(data.presentations || []);
      setStats({
        total_count: data.total_count || 0,
        total_storage_mb: data.total_storage_mb || 0,
        max_presentations: data.max_presentations || 50,
        max_storage_mb: data.max_storage_mb || 500
      });
    } catch (err) {
      console.error('Failed to load presentations:', err);
      setError('Failed to load presentations');
      // Set default stats on error
      setStats({
        total_count: 0,
        total_storage_mb: 0,
        max_presentations: 50,
        max_storage_mb: 500
      });
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async (presentationId, title) => {
    if (!window.confirm(`Delete "${title}"? This action cannot be undone.`)) return;

    try {
      await deletePresentation(presentationId);
      loadPresentations(); // Reload list
    } catch (err) {
      alert('Failed to delete presentation');
      console.error(err);
    }
  };

  const handleEdit = (presentationId) => {
    // Navigate to editor with presentation ID
    navigate(`/upload-setup/${presentationId}`);
  };

  const handlePresent = async (presentationId) => {
    try {
      const session = await createSession(presentationId);
      navigate(`/presenter/${session.id}`);
    } catch (err) {
      alert('Failed to start presentation');
      console.error(err);
    }
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  };

  const storagePercentage = stats.max_storage_mb > 0 
    ? ((stats.total_storage_mb / stats.max_storage_mb) * 100).toFixed(1) 
    : '0';
  const presentationsPercentage = stats.max_presentations > 0 
    ? ((stats.total_count / stats.max_presentations) * 100).toFixed(1) 
    : '0';

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 via-purple-50 to-pink-50">
      {/* Top Header with User Menu */}
      <header className="bg-white/80 backdrop-blur-md border-b sticky top-0 z-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-3 flex justify-between items-center">
          <button
            onClick={() => navigate('/upload-setup')}
            className="flex items-center gap-2 text-slate-600 hover:text-slate-900 transition-colors"
          >
            <ArrowLeft className="w-5 h-5" />
            <span className="text-sm font-medium">Back to Editor</span>
          </button>
          
          {/* User Profile Menu */}
          <div className="relative">
            <button
              onClick={() => setShowUserMenu(!showUserMenu)}
              className="w-10 h-10 rounded-full bg-gradient-to-r from-blue-600 to-purple-600 text-white font-semibold flex items-center justify-center hover:shadow-lg transition-all text-sm"
            >
              {getInitials(username)}
            </button>
            
            {showUserMenu && (
              <div className="absolute right-0 mt-2 w-56 bg-white rounded-lg shadow-xl border border-gray-200 py-2">
                <div className="px-4 py-3 border-b border-gray-100">
                  <p className="text-sm font-medium text-gray-900">{username}</p>
                  <p className="text-xs text-gray-500 truncate">Signed in</p>
                </div>
                <button
                  onClick={() => {
                    setShowUserMenu(false);
                    navigate('/settings');
                  }}
                  className="w-full px-4 py-2 text-left text-sm text-gray-700 hover:bg-gray-50 flex items-center gap-2"
                >
                  <Settings className="w-4 h-4" />
                  Account Settings
                </button>
                <button
                  onClick={() => {
                    setShowUserMenu(false);
                    handleLogout();
                  }}
                  className="w-full px-4 py-2 text-left text-sm text-red-600 hover:bg-red-50 flex items-center gap-2"
                >
                  <LogOut className="w-4 h-4" />
                  Logout
                </button>
              </div>
            )}
          </div>
        </div>
      </header>

      <div className="p-6">
        <div className="max-w-7xl mx-auto">
        {/* Header */}
        <div className="flex justify-between items-center mb-8">
          <div>
            <h1 className="text-4xl font-bold bg-gradient-to-r from-blue-600 to-purple-600 bg-clip-text text-transparent">
              My Presentations
            </h1>
            <p className="text-gray-600 mt-2">Manage and organize your presentations</p>
          </div>
          <Button
            onClick={() => navigate('/upload-setup')}
            className="bg-gradient-to-r from-blue-600 to-purple-600 hover:from-blue-700 hover:to-purple-700 gap-2"
          >
            <Plus className="w-4 h-4" />
            Create New
          </Button>
        </div>

        {/* Stats Cards */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-8">
          <Card>
            <CardHeader className="pb-3">
              <CardDescription>Total Presentations</CardDescription>
              <CardTitle className="text-3xl">
                {stats.total_count} / {stats.max_presentations}
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="w-full bg-gray-200 rounded-full h-2">
                <div
                  className="bg-gradient-to-r from-blue-600 to-purple-600 h-2 rounded-full transition-all"
                  style={{ width: `${presentationsPercentage}%` }}
                />
              </div>
              <p className="text-xs text-gray-500 mt-2">{presentationsPercentage}% used</p>
            </CardContent>
          </Card>

          <Card>
            <CardHeader className="pb-3">
              <CardDescription>Storage Used</CardDescription>
              <CardTitle className="text-3xl">
                {stats.total_storage_mb} MB
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="w-full bg-gray-200 rounded-full h-2">
                <div
                  className="bg-gradient-to-r from-green-500 to-emerald-600 h-2 rounded-full transition-all"
                  style={{ width: `${storagePercentage}%` }}
                />
              </div>
              <p className="text-xs text-gray-500 mt-2">{storagePercentage}% of {stats.max_storage_mb} MB</p>
            </CardContent>
          </Card>

          <Card>
            <CardHeader className="pb-3">
              <CardDescription>Available Space</CardDescription>
              <CardTitle className="text-3xl flex items-center gap-2">
                <HardDrive className="w-8 h-8 text-blue-600" />
                {(stats.max_storage_mb - stats.total_storage_mb).toFixed(1)} MB
              </CardTitle>
            </CardHeader>
            <CardContent>
              <p className="text-sm text-gray-600">
                {stats.max_presentations - stats.total_count} presentations remaining
              </p>
            </CardContent>
          </Card>
        </div>

        {/* Error Message */}
        {error && (
          <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded mb-6">
            {error}
          </div>
        )}

        {/* Presentations Grid */}
        {loading ? (
          <div className="text-center py-12">
            <div className="inline-block animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
            <p className="mt-4 text-gray-600">Loading presentations...</p>
          </div>
        ) : presentations.length === 0 ? (
          <Card>
            <CardContent className="text-center py-12">
              <Presentation className="w-16 h-16 mx-auto text-gray-400 mb-4" />
              <h3 className="text-xl font-semibold text-gray-700 mb-2">No presentations yet</h3>
              <p className="text-gray-500 mb-6">Create your first presentation to get started</p>
              <Button
                onClick={() => navigate('/upload-setup')}
                className="bg-gradient-to-r from-blue-600 to-purple-600 hover:from-blue-700 hover:to-purple-700"
              >
                <Plus className="w-4 h-4 mr-2" />
                Create Presentation
              </Button>
            </CardContent>
          </Card>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {presentations.map((pres) => (
              <Card key={pres.id} className="hover:shadow-lg transition-shadow">
                <CardHeader>
                  <CardTitle className="text-lg line-clamp-2">{pres.title}</CardTitle>
                  <CardDescription>
                    {pres.slide_count} slides • {formatDate(pres.updated_at)}
                  </CardDescription>
                </CardHeader>
                <CardContent>
                  <div className="flex gap-2">
                    <Button
                      size="sm"
                      variant="default"
                      onClick={() => handlePresent(pres.id)}
                      className="flex-1 bg-gradient-to-r from-blue-600 to-purple-600 hover:from-blue-700 hover:to-purple-700"
                    >
                      <Play className="w-4 h-4 mr-1" />
                      Present
                    </Button>
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={() => handleEdit(pres.id)}
                    >
                      <Edit className="w-4 h-4" />
                    </Button>
                    <Button
                      size="sm"
                      variant="destructive"
                      onClick={() => handleDelete(pres.id, pres.title)}
                    >
                      <Trash2 className="w-4 h-4" />
                    </Button>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        )}
        </div>
      </div>
    </div>
  );
};

export default PresentationsDashboard;
